import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { Shield, Target, TrendingUp, ChevronLeft, PlayCircle, CheckCircle, Circle, Loader2, FileText, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_META = {
    safe_start: { icon: Shield, color: '#3B82F6', bgClass: 'badge-phase-1' },
    building_consistency: { icon: Target, color: '#8B5CF6', bgClass: 'badge-phase-2' },
    capital_confidence: { icon: TrendingUp, color: '#D4AF37', bgClass: 'badge-phase-3' }
};

export default function CourseDetail() {
    const { courseId } = useParams();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const [course, setCourse] = useState(null);
    const [activeLesson, setActiveLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [markingComplete, setMarkingComplete] = useState(false);
    const [videoFailed, setVideoFailed] = useState(false);

    useEffect(() => { setVideoFailed(false); }, [activeLesson]);

    useEffect(() => {
        fetchCourse();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId, i18n.language]);

    const fetchCourse = async () => {
        try {
            const { data } = await axios.get(`${API}/courses/${courseId}`, {
                params: { lang: i18n.language },
                withCredentials: true
            });
            setCourse(data);
            if (data.lessons && data.lessons.length > 0) {
                // Set first incomplete lesson as active, or first lesson if all complete
                const incompleteLesson = data.lessons.find(l => !l.completed);
                setActiveLesson(incompleteLesson || data.lessons[0]);
            }
        } catch (e) {
            console.error('Error fetching course:', e);
            setError(e.response?.data?.detail || t('courseDetail.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const markComplete = async (lessonId) => {
        if (markingComplete) return;
        setMarkingComplete(true);
        
        try {
            await axios.post(`${API}/progress`, { lesson_id: lessonId }, { withCredentials: true });
            
            // Update local state
            setCourse(prev => ({
                ...prev,
                lessons: prev.lessons.map(l => 
                    l.id === lessonId ? { ...l, completed: true } : l
                )
            }));
            
            if (activeLesson?.id === lessonId) {
                setActiveLesson(prev => ({ ...prev, completed: true }));
            }
            
            toast.success(t('courseDetail.lessonCompleted'));
        } catch (e) {
            console.error('Error marking complete:', e);
            toast.error(t('courseDetail.errors.markFailed'));
        } finally {
            setMarkingComplete(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="card-surface p-12 text-center">
                        <p className="text-lg font-medium mb-4" style={{ color: 'var(--accent-danger)' }}>{error}</p>
                        <Link to="/courses">
                            <Button className="btn-secondary" data-testid="back-to-courses-btn">
                                <ChevronLeft className="w-4 h-4 mr-2 rtl-flip" />
                                {t('courseDetail.backToCourses')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const phaseInfo = PHASE_META[course?.phase];
    const completedCount = course?.lessons?.filter(l => l.completed).length || 0;
    const totalCount = course?.lessons?.length || 0;
    const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Back Link */}
                <Link to="/courses" className="inline-flex items-center gap-2 text-sm mb-6 hover:opacity-80 transition-opacity" style={{ color: 'var(--text-secondary)' }} data-testid="back-link">
                    <ChevronLeft className="w-4 h-4 rtl-flip" />
                    {t('courseDetail.backToCourses')}
                </Link>

                {/* Course Header */}
                <div className="card-surface p-6 mb-8" data-testid="course-header">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                        <div>
                            <span className={`${phaseInfo?.bgClass} px-2 py-1 rounded text-xs font-semibold inline-block mb-3`}>
                                {t(`phases.${course?.phase}.name`)}
                            </span>
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                {course?.title}
                            </h1>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-2xl font-bold" style={{ color: 'var(--primary-color)' }}>{progressPercent}%</p>
                            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('courseDetail.lessonsCount', { completed: completedCount, total: totalCount })}</p>
                        </div>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{course?.description}</p>
                    
                    {/* Progress Bar */}
                    <div className="mt-4 progress-track">
                        <div
                            className={`progress-fill ${course?.phase === 'safe_start' ? 'phase-1' : course?.phase === 'building_consistency' ? 'phase-2' : 'phase-3'}`}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Video Player Area */}
                    <div className="lg:col-span-2">
                        {activeLesson ? (
                            <div data-testid="video-player-section">
                                {/* Video Player */}
                                <div className="video-container mb-4">
                                    {activeLesson.video_file_url ? (
                                        videoFailed ? (
                                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center" style={{ backgroundColor: 'var(--background-elevated)' }} data-testid="video-error-fallback">
                                                <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{t('courseDetail.videoError')}</p>
                                                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{t('courseDetail.videoErrorHint')}</p>
                                                <a href={`${API}/files/${activeLesson.video_file_url}?download=1`} className="mt-2 inline-flex items-center px-4 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: 'var(--primary-color)', color: 'var(--primary-text)' }} data-testid="video-download-btn">
                                                    {t('courseDetail.videoDownload')}
                                                </a>
                                            </div>
                                        ) : (
                                        <video
                                            key={activeLesson.video_file_url}
                                            controls
                                            controlsList="nodownload"
                                            preload="metadata"
                                            src={`${API}/files/${activeLesson.video_file_url}`}
                                            className="w-full h-full"
                                            onError={() => setVideoFailed(true)}
                                            data-testid="self-hosted-video"
                                        >
                                            <track kind="captions" />
                                            Your browser doesn&apos;t support HTML5 video.
                                        </video>
                                        )
                                    ) : activeLesson.youtube_id ? (
                                        <iframe
                                            src={`https://www.youtube.com/embed/${activeLesson.youtube_id}?rel=0&hl=${(i18n.language || 'en').split('-')[0]}&cc_lang_pref=${(i18n.language || 'en').split('-')[0]}&cc_load_policy=1`}
                                            title={activeLesson.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            data-testid="youtube-player"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'var(--background-elevated)' }}>
                                            <p style={{ color: 'var(--text-muted)' }}>{t('courseDetail.videoUnavailable')}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Lesson Info */}
                                <div className="card-surface p-6">
                                    <div className="flex items-start justify-between gap-4 mb-4">
                                        <div>
                                            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                                                {activeLesson.title}
                                            </h2>
                                            {activeLesson.description && (
                                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                                    {activeLesson.description}
                                                </p>
                                            )}
                                        </div>
                                        {activeLesson.completed ? (
                                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                                <CheckCircle className="w-4 h-4" style={{ color: 'var(--accent-success)' }} />
                                                <span className="text-sm font-medium" style={{ color: 'var(--accent-success)' }}>{t('courseDetail.completed')}</span>
                                            </div>
                                        ) : (
                                            <Button
                                                onClick={() => markComplete(activeLesson.id)}
                                                disabled={markingComplete}
                                                className="btn-primary text-sm"
                                                data-testid="mark-complete-btn"
                                            >
                                                {markingComplete ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <CheckCircle className="w-4 h-4 mr-2" />
                                                        {t('courseDetail.markComplete')}
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </div>

                                    {/* Materials Section */}
                                    {activeLesson.materials && activeLesson.materials.length > 0 && (
                                        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-subtle)' }} data-testid="lesson-materials">
                                            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                                                <FileText className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                                                {t('courseDetail.downloadableMaterials')}
                                            </h3>
                                            <div className="space-y-2">
                                                {activeLesson.materials.map((material, idx) => {
                                                    const isUploaded = material.url && !material.url.startsWith('http');
                                                    const viewUrl = isUploaded ? `${API}/files/${material.url}` : material.url;
                                                    const downloadUrl = isUploaded ? `${API}/files/${material.url}?download=1` : material.url;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="flex items-center justify-between gap-3 p-3 rounded-lg transition-all group"
                                                            style={{ backgroundColor: 'var(--background-elevated)', border: '1px solid var(--border-subtle)' }}
                                                            data-testid={`material-link-${idx}`}
                                                        >
                                                            <a href={viewUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1">
                                                                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--primary-muted)' }}>
                                                                    <FileText className="w-4 h-4" style={{ color: 'var(--primary-color)' }} />
                                                                </div>
                                                                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                                                                    {material.title}
                                                                </p>
                                                            </a>
                                                            {isUploaded ? (
                                                                <a href={downloadUrl} className="flex-shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded transition-all" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)', border: '1px solid var(--border-highlight)' }} data-testid={`material-download-${idx}`}>
                                                                    <Download className="w-3 h-3" />
                                                                    Download
                                                                </a>
                                                            ) : (
                                                                <ExternalLink className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="card-surface p-12 text-center">
                                <PlayCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                                <p className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>{t('courseDetail.noLessons')}</p>
                                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('courseDetail.noLessonsDesc')}</p>
                            </div>
                        )}
                    </div>

                    {/* Lesson List Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card-surface p-4">
                            <h3 className="font-semibold mb-4 px-2" style={{ color: 'var(--text-primary)' }}>
                                {t('courseDetail.courseContent')}
                            </h3>
                            
                            <div className="space-y-1" data-testid="lessons-list">
                                {course?.lessons?.map((lesson, index) => (
                                    <button
                                        key={lesson.id}
                                        onClick={() => setActiveLesson(lesson)}
                                        className={`lesson-item w-full text-left ${activeLesson?.id === lesson.id ? 'active' : ''} ${lesson.completed ? 'completed' : ''}`}
                                        data-testid={`lesson-item-${lesson.id}`}
                                    >
                                        <div className="flex items-center gap-3 w-full">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: lesson.completed ? 'rgba(16, 185, 129, 0.1)' : 'var(--background-base)' }}>
                                                {lesson.completed ? (
                                                    <CheckCircle className="w-4 h-4" style={{ color: 'var(--accent-success)' }} />
                                                ) : (
                                                    <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{index + 1}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium truncate ${activeLesson?.id === lesson.id ? 'text-primary' : ''}`} style={{ color: activeLesson?.id === lesson.id ? 'var(--primary-color)' : 'var(--text-primary)' }}>
                                                    {lesson.title}
                                                </p>
                                            </div>
                                            {activeLesson?.id === lesson.id && (
                                                <PlayCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-color)' }} />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
